import { useEffect, useState } from "react";
import { X, ArrowUpRight, PhoneCall } from "lucide-react";
import { BUSINESS } from "@/lib/siteData";

const SESSION_KEY = "rp_mascot_seen_v1";

/**
 * Floating "Robby" mascot that walks across the bottom of the page on first
 * load, hops up near the header phone number, points at it, then fades away.
 *
 * Stages (state machine):
 *   walk-in   → mascot slides in from off-screen bottom-left
 *   walking   → travels along the bottom to just under the phone CTA
 *   pointing  → jumps up and points; speech bubble appears
 *   leaving   → mascot fades out
 *   done      → unmounted
 *
 * Only shows once per browser session. Never overlaps with the cookie banner
 * or the lead modal (positioned above them via z-index only when pointing).
 * Fully hidden for users with prefers-reduced-motion.
 */
export default function FloatingMascot() {
  const [stage, setStage] = useState("hidden");

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Respect reduced motion
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    // Once-per-session gate
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return;
    } catch { /* ignore */ }

    const timers = [];
    // Wait until page is settled + cookie banner has had a moment
    timers.push(setTimeout(() => setStage("walk-in"), 3500));
    timers.push(setTimeout(() => setStage("walking"), 4200));
    timers.push(setTimeout(() => setStage("pointing"), 8800));
    timers.push(setTimeout(() => setStage("leaving"), 15000));
    timers.push(setTimeout(() => {
      setStage("done");
      try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
    }, 16500));

    return () => timers.forEach(clearTimeout);
  }, []);

  const dismiss = () => {
    setStage("done");
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
  };

  if (stage === "hidden" || stage === "done") return null;

  const isPointing = stage === "pointing";
  const isLeaving = stage === "leaving";

  // Position + transform per stage
  const wrapperStyle = {
    // Header phone button sits roughly ~1050px from left on desktop; we anchor
    // the mascot to bottom-right by default and animate up near the header.
    right: isPointing || isLeaving ? "180px" : undefined,
    left: stage === "walk-in" ? "-140px" : stage === "walking" ? "50%" : undefined,
    top: isPointing || isLeaving ? "60px" : undefined,
    bottom: !(isPointing || isLeaving) ? "20px" : undefined,
    transform: stage === "walking" ? "translateX(-50%)" : "none",
    opacity: isLeaving ? 0 : 1,
    transition:
      "left 4.2s cubic-bezier(.22,.61,.36,1), right 1.6s ease-in-out, top 1.6s ease-in-out, bottom 1.6s ease-in-out, opacity 1.4s ease-out, transform 4.2s ease-in-out",
    // Keep it under the cookie banner (z-70) but above regular page content
    zIndex: 65,
    pointerEvents: isPointing ? "auto" : "none",
  };

  return (
    <div
      data-testid="floating-mascot"
      aria-hidden={!isPointing}
      className="fixed hidden md:block"
      style={wrapperStyle}
    >
      <div className="relative flex flex-col items-center">
        {/* Speech bubble (only when pointing) */}
        {isPointing && (
          <div
            data-testid="floating-mascot-bubble"
            className="absolute -top-16 right-0 bg-card border border-border rounded-md shadow-xl px-4 py-2.5 flex items-center gap-2 whitespace-nowrap animate-in fade-in slide-in-from-top-2 duration-500"
            style={{ pointerEvents: "auto" }}
          >
            <PhoneCall className="h-4 w-4 text-primary shrink-0" />
            <div className="min-w-0">
              <a
                href={BUSINESS.phoneHref}
                data-testid="floating-mascot-phone-link"
                className="text-sm font-heading font-bold text-foreground hover:text-primary leading-tight"
              >
                {BUSINESS.phone}
              </a>
            </div>
            <ArrowUpRight className="h-4 w-4 text-primary shrink-0" />
            <button
              data-testid="floating-mascot-close"
              onClick={dismiss}
              aria-label="Dismiss mascot"
              className="ml-1 h-6 w-6 inline-flex items-center justify-center rounded hover:bg-secondary text-muted-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            {/* Tail pointing down toward the mascot */}
            <span className="absolute -bottom-2 right-6 w-3 h-3 bg-card border-b border-r border-border rotate-45" />
          </div>
        )}

        {/* Mascot itself — mirrored when walking so it faces the direction of travel */}
        <img
          src="/logo-mascot.png"
          alt=""
          className={`select-none ${stage === "walk-in" || stage === "walking" ? "rp-mascot-walk" : ""}`}
          style={{
            width: 96,
            height: "auto",
            transform: stage === "walk-in" || stage === "walking" ? "scaleX(1)" : "scaleX(-1)",
            transition: "transform 0.4s ease",
            filter: "drop-shadow(0 6px 10px rgba(0,0,0,0.18))",
            pointerEvents: isPointing ? "auto" : "none",
          }}
          draggable="false"
        />
      </div>
    </div>
  );
}
